import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Bike, CheckCircle, AlertCircle, Loader2, Clock, UserCheck, MapPin, CalendarClock, AlertTriangle } from "lucide-react";

const NO_FLEX_RE = /^\s*(no|none|nope|nah|n\/a|na|not flexible|no flexibility|not|nothing|no thanks)\s*\.?\s*$/i;
function isFlexible(text) {
  if (!text || !text.trim()) return false;
  return !NO_FLEX_RE.test(text.trim());
}

export default function ClaimPage() {
  const { token } = useParams();

  const [booking, setBooking]           = useState(null);
  const [loading, setLoading]           = useState(true);
  const [claiming, setClaiming]         = useState(false);
  const [claimed, setClaimed]           = useState(false);
  const [claimedByName, setClaimedByName] = useState("");
  const [claimedBike, setClaimedBike]   = useState("");
  const [fullyConfirmed, setFullyConfirmed] = useState(false);
  const [error, setError]               = useState(null);

  // Flexibility interstitial
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [selectedTime, setSelectedTime]     = useState("");

  // Dual-bike: bike selection step
  const [showBikeSelect, setShowBikeSelect] = useState(false);
  const [selectedBike, setSelectedBike]     = useState(""); // "trishaw" | "wheelchair"

  // Time-conflict feedback (when volunteer picks a time that clashes with another booking)
  const [conflictInfo, setConflictInfo] = useState(null); // {start, end} or null

  const formatConflictWindow = (startIso, endIso) => {
    if (!startIso || !endIso) return "";
    const opts = { hour: "2-digit", minute: "2-digit", hour12: false };
    return `${new Date(startIso).toLocaleTimeString([], opts)}–${new Date(endIso).toLocaleTimeString([], opts)}`;
  };

  useEffect(() => {
    api.getClaimDetails(token)
      .then((r) => {
        setBooking(r.data);
        if (r.data.booking_datetime_iso) {
          const d = new Date(r.data.booking_datetime_iso);
          setSelectedTime(`${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`);
        }
      })
      .catch((err) => {
        const status = err.response?.status;
        const detail = err.response?.data?.detail || "";
        if (status === 410) {
          setError("This claim link has expired. Please contact bookings@doddervalleycycling.com if you still wish to volunteer for this booking.");
        } else if (status === 404) {
          setError("Booking not found. It may have been cancelled or removed. Please contact bookings@doddervalleycycling.com.");
        } else {
          setError(detail || "Failed to load booking details. Please try again or contact bookings@doddervalleycycling.com.");
        }
      })
      .finally(() => setLoading(false));
  }, [token]);

  const isDual     = booking?.bike_choice === "Trishaw and Wheelchair Bike";
  const isWheelchair = booking?.bike_choice === "Wheelchair Bike Only";
  const volunteer  = booking?.volunteer;

  // Step 1 of claim — determine what to show first
  const handleClaimClick = () => {
    if (isDual) {
      setShowBikeSelect(true);
      return;
    }
    // Standard / wheelchair / trishaw booking
    if (isFlexible(booking?.time_flexibility)) {
      setShowTimePicker(true);
    } else {
      doClaim(null, null);
    }
  };

  // After bike selection, proceed
  const handleBikeSelected = (bike) => {
    setSelectedBike(bike);
    setShowBikeSelect(false);
    doClaim(null, bike);
  };

  const buildTimeOverride = (localTimeStr) => {
    if (!localTimeStr || !booking?.booking_datetime_iso) return null;
    const orig = new Date(booking.booking_datetime_iso);
    const [h, m] = localTimeStr.split(":").map(Number);
    return new Date(orig.getFullYear(), orig.getMonth(), orig.getDate(), h, m, 0, 0).toISOString();
  };

  const doClaim = async (timeOverride, bike) => {
    setClaiming(true);
    setConflictInfo(null);
    try {
      const res = await api.claimBooking(token, timeOverride, bike || null);
      setClaimedByName(res.data.volunteer_name);
      setClaimedBike(res.data.bike ? (res.data.bike === "trishaw" ? "Trishaw" : "Wheelchair Bike") : "");
      setFullyConfirmed(res.data.fully_confirmed ?? true);
      setClaimed(true);
      toast.success("Booking claimed successfully!");
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : (detail?.message || "Failed to claim booking.");
      toast.error(msg);
      if (err.response?.status === 409) {
        if (detail && typeof detail === "object" && detail.code === "time_conflict") {
          // Volunteer picked a time that clashes with another booking — keep the time
          // picker open with a red banner explaining the conflict window.
          setConflictInfo({
            start: detail.conflict_start,
            end: detail.conflict_end,
            free_slots: Array.isArray(detail.free_slots) ? detail.free_slots : [],
          });
          setShowTimePicker(true);
        } else {
          // Booking was genuinely claimed by another volunteer meanwhile.
          setBooking((prev) => ({ ...prev, already_claimed: true }));
          setShowTimePicker(false);
          setShowBikeSelect(false);
        }
      }
    } finally {
      setClaiming(false);
    }
  };

  return (
    <div style={{ fontFamily: "'Public Sans', sans-serif" }} className="min-h-screen bg-[#fcfdfc] flex flex-col">
      <div className="bg-[#15803d] text-white py-5 px-6">
        <div className="max-w-xl mx-auto flex items-center gap-3">
          <img src="/favicon.png" alt="DVC" className="w-7 h-7" />
          <div>
            <h1 className="text-xl font-bold" style={{ fontFamily: "'Outfit', sans-serif" }}>Dodder Valley Cycling</h1>
            <p className="text-green-200 text-sm">Accessible Bike Booking System</p>
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-start justify-center px-4 py-12">
        <div className="max-w-lg w-full">
          {loading && (
            <div className="text-center py-16">
              <Loader2 className="w-10 h-10 animate-spin text-green-700 mx-auto mb-4" />
              <p className="text-slate-500">Loading booking details...</p>
            </div>
          )}

          {!loading && error && (
            <div className="bg-white rounded-2xl border border-red-100 shadow-sm p-8 text-center" data-testid="claim-error">
              <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-slate-800 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Booking Not Found</h2>
              <p className="text-slate-500">{error}</p>
            </div>
          )}

          {!loading && !error && booking?.already_claimed && !claimed && (
            <div className="bg-white rounded-2xl border border-amber-100 shadow-sm p-8 text-center" data-testid="already-claimed">
              <Clock className="w-12 h-12 text-amber-400 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-slate-800 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Already Claimed</h2>
              <p className="text-slate-500">This booking has already been claimed by another volunteer.</p>
              {booking.claimed_by_name && <p className="text-slate-400 text-sm mt-2">Claimed by: {booking.claimed_by_name}</p>}
            </div>
          )}

          {/* ── DUAL-BIKE SELECTION ── */}
          {!loading && !error && booking && !booking.already_claimed && !claimed && showBikeSelect && (
            <div className="bg-white rounded-2xl border border-green-100 shadow-sm overflow-hidden animate-in fade-in duration-300" data-testid="bike-select-step">
              <div className="bg-green-50 border-b border-green-100 px-6 py-5">
                <h2 className="text-xl font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>Which bike are you claiming?</h2>
                <p className="text-slate-500 text-sm mt-1">This is a group booking — select the bike you will assist with.</p>
              </div>
              <div className="p-6 space-y-4">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Availability</p>
                <div className="space-y-3">
                  <button
                    disabled={booking.trishaw_claimed}
                    onClick={() => !booking.trishaw_claimed && handleBikeSelected("trishaw")}
                    data-testid="select-trishaw-btn"
                    className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                      booking.trishaw_claimed
                        ? "border-slate-100 bg-slate-50 text-slate-400 cursor-not-allowed"
                        : "border-green-300 bg-green-50 hover:border-green-500 hover:bg-green-100 cursor-pointer"
                    }`}
                  >
                    <div className="font-semibold text-sm">{booking.trishaw_claimed ? "Trishaw — Already claimed" : "Trishaw"}</div>
                    {booking.trishaw_claimed && <div className="text-xs mt-0.5">Claimed by {booking.trishaw_volunteer_name}</div>}
                    {!booking.trishaw_claimed && <div className="text-xs text-green-600 mt-0.5">Available — click to claim</div>}
                  </button>
                  <button
                    disabled={booking.wheelchair_claimed || !booking.vol_wheelchair_license}
                    onClick={() => !booking.wheelchair_claimed && booking.vol_wheelchair_license && handleBikeSelected("wheelchair")}
                    data-testid="select-wheelchair-btn"
                    className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                      booking.wheelchair_claimed || !booking.vol_wheelchair_license
                        ? "border-slate-100 bg-slate-50 text-slate-400 cursor-not-allowed"
                        : "border-blue-300 bg-blue-50 hover:border-blue-500 hover:bg-blue-100 cursor-pointer"
                    }`}
                  >
                    <div className="font-semibold text-sm">{booking.wheelchair_claimed ? "Wheelchair Bike — Already claimed" : "Wheelchair Bike"}</div>
                    {booking.wheelchair_claimed && <div className="text-xs mt-0.5">Claimed by {booking.wheelchair_volunteer_name}</div>}
                    {!booking.wheelchair_claimed && !booking.vol_wheelchair_license && (
                      <div className="text-xs text-slate-400 mt-0.5">Not available to be claimed</div>
                    )}
                    {!booking.wheelchair_claimed && booking.vol_wheelchair_license && <div className="text-xs text-blue-600 mt-0.5">Available — click to claim</div>}
                  </button>
                </div>
                <Button variant="outline" onClick={() => setShowBikeSelect(false)} className="w-full mt-2 rounded-full border-slate-200 text-slate-600">
                  Back
                </Button>
              </div>
            </div>
          )}

          {/* ── TIME PICKER INTERSTITIAL ── */}
          {!loading && !error && booking && !booking.already_claimed && !claimed && showTimePicker && (
            <div className="bg-white rounded-2xl border border-amber-100 shadow-sm overflow-hidden animate-in fade-in duration-300" data-testid="time-picker-interstitial">
              <div className="bg-amber-50 border-b border-amber-100 px-6 py-5">
                <div className="flex items-center gap-2 mb-1">
                  <CalendarClock className="w-5 h-5 text-amber-600" />
                  <h2 className="text-xl font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>Requester Has Time Flexibility</h2>
                </div>
                <p className="text-slate-500 text-sm">Please review their availability and set a confirmed time.</p>
              </div>
              <div className="p-6 space-y-5">
                <div className="bg-slate-50 rounded-xl border border-slate-200 p-4">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Requester's availability</p>
                  <p className="text-slate-700 text-sm italic leading-relaxed">"{booking.time_flexibility}"</p>
                </div>
                <div className={`${conflictInfo ? "bg-red-50 border-red-200 text-red-800" : "bg-amber-50 border-amber-200 text-amber-800"} border rounded-xl p-4 text-sm`} data-testid={conflictInfo ? "conflict-banner" : "flex-banner"}>
                  {conflictInfo ? (
                    <div className="space-y-3">
                      <div>
                        <strong>Time conflict.</strong> The time you selected overlaps with another confirmed booking (<strong>{formatConflictWindow(conflictInfo.start, conflictInfo.end)}</strong>). Please pick a time outside that window, or use <strong>Back</strong> to claim the original slot.
                      </div>
                      {Array.isArray(conflictInfo.free_slots) && conflictInfo.free_slots.length > 0 && (
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-wide mb-1.5">Free suggested times on this day</p>
                          <div className="flex flex-wrap gap-1.5" data-testid="conflict-free-slots">
                            {conflictInfo.free_slots.map((t) => (
                              <button key={t} type="button" data-testid={`free-slot-${t}`}
                                onClick={() => { setSelectedTime(t); setConflictInfo(null); }}
                                className="px-2.5 py-1 rounded-full bg-white border border-red-300 text-red-800 text-xs font-medium hover:bg-red-100 transition-colors">
                                {t}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      {Array.isArray(conflictInfo.free_slots) && conflictInfo.free_slots.length === 0 && (
                        <p className="text-xs italic">No other free slots on this day.</p>
                      )}
                    </div>
                  ) : (
                    "The requester has indicated they are flexible on the day. Please modify the time within the time range they have indicated."
                  )}
                </div>
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Booking Date (fixed)</p>
                  <p className="font-semibold text-slate-800">{booking.booking_dt_formatted?.split(" at ")[0]}</p>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 block" htmlFor="vol-time-pick">Select Confirmed Time</label>
                  <input id="vol-time-pick" type="time" data-testid="volunteer-time-input"
                    value={selectedTime} onChange={(e) => { setSelectedTime(e.target.value); setConflictInfo(null); }}
                    className="h-12 w-full border border-slate-200 rounded-xl px-4 text-base bg-white focus:outline-none focus:border-green-600" />
                  <p className="text-xs text-slate-400 mt-1.5">Original requested time was: <strong>{booking.booking_dt_formatted?.split(" at ")[1]}</strong></p>
                </div>
                <div className="flex gap-3 pt-2">
                  <Button variant="outline" onClick={() => { setShowTimePicker(false); setConflictInfo(null); }} className="flex-1 rounded-full border-slate-200 text-slate-600">Back</Button>
                  <Button data-testid="confirm-time-claim-btn" disabled={claiming || !selectedTime}
                    onClick={() => doClaim(buildTimeOverride(selectedTime), isDual ? selectedBike : null)}
                    className="flex-1 h-12 text-base font-semibold rounded-full bg-[#ea580c] hover:bg-orange-700 text-white shadow-lg transition-all">
                    {claiming ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : null}
                    {claiming ? "Confirming..." : "Confirm Time & Claim"}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* ── NORMAL CLAIM VIEW ── */}
          {!loading && !error && booking && !booking.already_claimed && !claimed && !showTimePicker && !showBikeSelect && (
            <div className="bg-white rounded-2xl border border-green-100 shadow-sm overflow-hidden" data-testid="claim-form">
              <div className="bg-[#f0fdf4] border-b border-green-100 p-6">
                {volunteer ? (
                  <>
                    <div className="flex items-center gap-2 mb-2">
                      <UserCheck className="w-5 h-5 text-green-600" />
                      <span className="text-sm font-semibold text-green-700">Hi, {volunteer.name}!</span>
                    </div>
                    <h2 className="text-2xl font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>Are you available?</h2>
                    <p className="text-slate-500 text-sm mt-1">Claim this booking if you can assist.</p>
                  </>
                ) : (
                  <>
                    <h2 className="text-2xl font-bold text-slate-800 mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>Volunteer Available?</h2>
                    <p className="text-slate-500 text-sm">Claim this booking if you are available to assist.</p>
                  </>
                )}
              </div>

              <div className="p-6">
                <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">Booking Details</h3>
                <div className="bg-slate-50 rounded-xl p-4 space-y-2.5 mb-4">
                  <DetailRow label="Requester" value={booking.requester_name} />
                  <DetailRow label="Date & Time" value={booking.booking_dt_formatted} />
                  <DetailRow label="Duration" value={`${booking.duration_hours} hour(s)`} />
                  {booking.meeting_point && (
                    <div className="flex items-start justify-between gap-2 pt-1 border-t border-slate-200">
                      <span className="text-slate-500 text-sm flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> Meeting Point</span>
                      <span className="font-semibold text-slate-800 text-sm text-right max-w-[60%]">{booking.meeting_point}</span>
                    </div>
                  )}
                  {booking.bike_choice && (
                    <div className="flex items-start justify-between gap-2 pt-1 border-t border-slate-200">
                      <span className="text-slate-500 text-sm shrink-0">Bike Choice</span>
                      <span className="text-green-700 font-semibold text-sm text-right max-w-[65%]">{booking.bike_choice}</span>
                    </div>
                  )}
                  {booking.time_flexibility && (
                    <div className="flex items-start justify-between gap-2 pt-1 border-t border-slate-200">
                      <span className="text-slate-500 text-sm shrink-0">Flexibility</span>
                      <span className={`text-sm text-right max-w-[65%] ${isFlexible(booking.time_flexibility) ? "text-amber-700 font-semibold" : "text-slate-600"}`}>
                        {booking.time_flexibility}{isFlexible(booking.time_flexibility) && " ⚡"}
                      </span>
                    </div>
                  )}
                  {isDual && (
                    <div className="pt-2 border-t border-slate-200 space-y-1.5">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Bike Availability</p>
                      <div className={`flex justify-between text-sm rounded-lg px-3 py-1.5 ${booking.trishaw_claimed ? "bg-slate-100 text-slate-400" : "bg-green-50 text-green-700"}`}>
                        <span>Trishaw</span><span>{booking.trishaw_claimed ? `Claimed by ${booking.trishaw_volunteer_name}` : "Available"}</span>
                      </div>
                      <div className={`flex justify-between text-sm rounded-lg px-3 py-1.5 ${booking.wheelchair_claimed ? "bg-slate-100 text-slate-400" : "bg-blue-50 text-blue-700"}`}>
                        <span>Wheelchair Bike</span><span>{booking.wheelchair_claimed ? `Claimed by ${booking.wheelchair_volunteer_name}` : "Available"}</span>
                      </div>
                    </div>
                  )}
                </div>

                {isWheelchair && !booking.vol_wheelchair_license && volunteer && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4 text-sm text-red-700 flex items-start gap-2" data-testid="no-wheelchair-license-notice">
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-red-500" />
                    <span>You are not licensed to operate the Wheelchair Bike. You cannot claim this booking.</span>
                  </div>
                )}

                {isFlexible(booking.time_flexibility) && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4 text-sm text-amber-800 flex items-start gap-2" data-testid="flexibility-notice">
                    <CalendarClock className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>This requester is flexible on timing. Claiming will let you confirm an alternative time.</span>
                  </div>
                )}

                {volunteer ? (
                  <Button data-testid="claim-booking-btn" disabled={claiming || (isWheelchair && !booking.vol_wheelchair_license)} onClick={handleClaimClick}
                    className="w-full h-12 text-base font-semibold rounded-full bg-[#ea580c] hover:bg-orange-700 text-white shadow-lg transition-all hover:-translate-y-0.5">
                    {claiming ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : null}
                    {claiming ? "Confirming..." : isDual ? "Select Bike & Claim" : "Yes, I'll Take This Booking"}
                  </Button>
                ) : (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-center text-amber-700 text-sm" data-testid="no-vol-id-warning">
                    <AlertCircle className="w-5 h-5 mx-auto mb-2" />
                    This link does not include volunteer identification. Please use the personalised link sent to your email.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── SUCCESS ── */}
          {claimed && (
            <div className="bg-white rounded-2xl border border-green-100 shadow-sm p-8 text-center animate-in fade-in duration-500" data-testid="claim-success">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-800 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                {fullyConfirmed ? "Booking Confirmed!" : "Bike Claimed!"}
              </h2>
              <p className="text-slate-600 mb-2">Thank you, <strong>{claimedByName}</strong>!</p>
              {claimedBike && !fullyConfirmed && (
                <p className="text-slate-500 text-sm mb-2">You have claimed the <strong>{claimedBike}</strong>. The other bike is still pending — another volunteer will be notified.</p>
              )}
              {claimedBike && fullyConfirmed && (
                <p className="text-slate-500 text-sm mb-2">You have claimed the <strong>{claimedBike}</strong>. Both bikes are now confirmed!</p>
              )}
              {!claimedBike && (
                <p className="text-slate-500 text-sm">The requester has been notified and will receive a confirmation email.</p>
              )}
              <p className="text-slate-400 text-sm mt-1">You will receive reminder emails 24 hours and 2 hours before the booking.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-slate-500 text-sm">{label}</span>
      <span className="font-semibold text-slate-800 text-sm text-right">{value}</span>
    </div>
  );
}
